import { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { apiErrorMessage, formatTime } from '../../../i18n';
import { api } from '../../../lib/api';

/** Label of the player's code-entry option; the TV player shows it in English. */
const PLAYER_CODE_OPTION = 'Have a code from the dashboard instead?';

type ManualPairingCodeProps = {
  organisationId: string;
  screenId: string;
  /** Fetch a code as soon as this panel is shown (Can't scan → code). */
  autoRequest?: boolean;
};

/** Fallback when the TV can't be scanned: issue a code to type on the player. */
export function ManualPairingCode({
  organisationId,
  screenId,
  autoRequest = false,
}: ManualPairingCodeProps) {
  const { t } = useTranslation(['displays', 'common']);
  const [pairing, setPairing] = useState<{ code: string; expiresAt: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const autoRequested = useRef(false);

  const requestCode = useMutation({
    mutationFn: () => api.createPairingCode(organisationId, screenId),
    onSuccess: (result) => {
      setError(null);
      setPairing(result);
    },
    onError: (err) => {
      setPairing(null);
      setError(apiErrorMessage(err, t('pairing.code.createError')));
    },
  });

  const requestPairingCode = requestCode.mutate;

  useEffect(() => {
    if (!autoRequest || autoRequested.current) return;
    autoRequested.current = true;
    requestPairingCode();
  }, [autoRequest, requestPairingCode]);

  const copyCode = async () => {
    if (!pairing) return;
    try {
      await navigator.clipboard.writeText(pairing.code);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — code is still visible */
    }
  };

  return (
    <div className="space-y-3 rounded-[10px] border border-line bg-panel2 px-3 py-3">
      <p className="m-0 text-sm text-muted">
        <Trans
          t={t}
          i18nKey="pairing.code.instructions"
          values={{ option: PLAYER_CODE_OPTION }}
          components={{ strong: <span className="font-semibold text-ink" /> }}
        />
      </p>
      {error ? (
        <p className="m-0 text-sm font-medium" style={{ color: 'var(--gs-bad)' }} role="alert">
          {error}
        </p>
      ) : null}
      {requestCode.isPending && !pairing ? (
        <p className="m-0 text-sm text-muted">{t('pairing.code.generating')}</p>
      ) : null}
      {pairing ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <p className="m-0 font-mono text-3xl tracking-[0.2em] text-ink">{pairing.code}</p>
          <div className="space-y-1">
            <p className="m-0 text-xs text-muted">
              {t('pairing.code.expires', { time: formatTime(pairing.expiresAt) })}
            </p>
            <button
              type="button"
              onClick={() => void copyCode()}
              className="text-sm font-semibold text-signal hover:underline"
            >
              {copied ? t('common:actions.copied') : t('pairing.code.copy')}
            </button>
          </div>
        </div>
      ) : null}
      {pairing || error ? (
        <button
          type="button"
          onClick={() => requestCode.mutate()}
          disabled={requestCode.isPending}
          className="rounded-[10px] border border-line bg-panel px-4 py-2.5 text-sm font-semibold text-ink hover:border-[var(--gs-accent)] disabled:opacity-60"
        >
          {requestCode.isPending ? t('pairing.code.generating') : t('pairing.code.newCode')}
        </button>
      ) : null}
    </div>
  );
}
